import { useEffect, useRef } from 'react';

/**
 * 폼·확인을 담는 모달.
 *
 * 이 저장소에는 모달이 하나도 없었다 — 설정 화면의 등록·수정이 `window.prompt`를 4~6번 **연쇄로**
 * 띄웠다. 그 방식은 (1) 폼 전체를 한눈에 볼 수 없고, (2) 어떤 값이 허용되는지 알려줄 자리가 없고,
 * (3) 중간에 Esc를 누르면 앞에서 입력한 것이 통째로 사라지고, (4) Cancel의 `null`을 값으로 착각하는
 * 사고를 반복해서 만들었다(각 화면 주석에 그 이력이 남아 있다).
 *
 * 다섯 개 설정 화면이 **이 하나를 공유한다** — 화면마다 새로 만들지 않는다(ddd-design-lint F5).
 * 보드와 AI-DART의 폼이 같은 셸을 사용한다.
 */
export function Modal({
  open, onClose, title, children,
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element | null {
  /*
   * `onClose`를 **의존성으로 걸지 않는다.** 호출부는 `onClose={() => setForm(null)}`처럼 매 렌더 새
   * 함수를 넘기므로, 의존성에 넣으면 폼에 글자를 한 자 칠 때마다 이펙트가 다시 돌아 document 리스너를
   * 떼었다 붙이고 `body.overflow`를 토글한다. 최신 함수는 ref로 읽는다 — 이펙트는 열림/닫힘에만 반응한다.
   */
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => { if (event.key === 'Escape') closeRef.current(); };
    document.addEventListener('keydown', onKey);
    /*
     * 배경 스크롤 잠금. 풀 때 `''`로 덮지 않고 **원래 값을 복원**한다 — 모달이 겹쳐 열리면
     * 안쪽이 닫힐 때 바깥쪽의 잠금까지 풀려 배경이 스크롤된다(인입 키 화면이 발급·폐기 두 모달을 갖는다).
     */
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="mt-[8vh] mb-8 w-full max-w-xl rounded-xl border border-line bg-surface p-5 shadow-xl"
        /* 오버레이의 닫기가 내용 클릭에도 걸리면 입력 중에 폼이 사라진다. */
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-[13px] font-bold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="cursor-pointer rounded-md px-1.5 text-[15px] leading-none text-faint transition hover:bg-line-soft hover:text-ink"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * 되돌릴 수 없는 조작의 확인 — `window.confirm`을 대신한다.
 *
 * 네이티브 confirm을 쓰면 (1) 무엇을 지우는지 서식 있게 보여줄 수 없고, (2) 실패 사유를 그 자리에
 * 띄울 수 없어 화면 뒤쪽 메시지 줄로 밀리고, (3) 브라우저마다 버튼 순서가 달라 위험한 쪽이 기본이 된다.
 */
export function ConfirmModal({
  open, title, confirmLabel, busy, onConfirm, onClose, children,
}: {
  open: boolean;
  title: string;
  confirmLabel: string;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children: React.ReactNode;
}): React.JSX.Element | null {
  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="mt-3 text-[12.5px] leading-relaxed">{children}</div>
      <div className="mt-5 flex justify-end gap-2 border-t border-line pt-4">
        {/* `shared`는 기능 폴더를 import하지 않는다 — AI-DART `ActionButton`(default·danger)의 모양을 그대로 옮겨 적는다. */}
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer rounded-md border border-line bg-surface px-2.5 py-1 text-[11px] whitespace-nowrap text-muted transition hover:border-accent-line hover:bg-line-soft hover:text-accent active:bg-line disabled:cursor-not-allowed disabled:opacity-40"
        >취소</button>
        <button
          type="button"
          disabled={busy}
          onClick={onConfirm}
          className="cursor-pointer rounded-md border border-rose-200 bg-white px-2.5 py-1 text-[11px] whitespace-nowrap text-rose-600 transition hover:border-rose-300 hover:bg-rose-50 active:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-40"
        >{confirmLabel}</button>
      </div>
    </Modal>
  );
}
